import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactForm from '@/components/feature/ContactForm';
import { useSeo } from '@/hooks/useSeo';
import { siteInfo } from '@/mocks/site';
import { testimonials } from '@/mocks/testimonials';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '只整理可查证的公开信息',
    text: '站内话题均以公开可查的信息与普遍行业观察为基础，不挖掘未公开的私人信息，也不转载来源不明的截图与音频。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '不对真实个人做定性',
    text: '我们讨论现象与机制，不给具体个人贴标签、下结论。涉及个人的内容，一律只围绕公开行为展开。',
  },
  {
    icon: 'ri-file-list-3-line',
    title: '分类清晰，看重阅读价值',
    text: '每篇内容都要能被放进一个明确的分区，并且读完确实有信息增量，而不是把公告改写成一段更长的公告。',
  },
  {
    icon: 'ri-refresh-line',
    title: '有错就改，欢迎纠错',
    text: '如果发现站内表述与公开事实不符，欢迎通过表单反馈。我们会在核实后及时修订并说明修改内容。',
  },
];

const stats = [
  { value: '5', label: '内容分区' },
  { value: '15+', label: '话题整理' },
  { value: '2021', label: '开始整理' },
  { value: '工作日', label: '来信回复' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 瓜田爆料的定位与内容原则',
    description:
      '了解瓜田爆料的网站定位、发展理念与内容原则。我们按分类合集整理娱乐圈、网红圈、素人瓜与行业秘闻，坚持不发布未核实信息、不对真实个人定性，并提供联系与投稿方式。',
    keywords: '瓜田爆料,关于我们,内容原则,网站定位,投稿联系',
    canonicalPath: '/about',
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于瓜田爆料',
        url: 'https://yoyoermei.cn/about',
        inLanguage: 'zh-CN',
        description: siteInfo.description,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        url: 'https://yoyoermei.cn/',
        email: siteInfo.email,
        foundingDate: siteInfo.since,
        description: siteInfo.tagline,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系瓜田爆料',
        url: 'https://yoyoermei.cn/about#contact',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yoyoermei.cn/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
            item: 'https://yoyoermei.cn/about',
          },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-background-50 border border-background-300 px-3.5 py-1.5 text-xs text-foreground-700">
                <i className="ri-plant-line text-primary-500"></i>
                关于我们
              </span>
              <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold leading-snug text-foreground-950">
                瓜田爆料，把话题整理成合集的内容站
              </h1>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
                {siteInfo.description}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#categories"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-layout-grid-line"></i>
                  浏览分类合集
                </Link>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-foreground-300 text-foreground-900 text-sm font-medium hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  联系与投稿
                </a>
              </div>
            </div>
            <div className="w-full h-56 md:h-72 lg:h-80 rounded-lg overflow-hidden border border-background-300 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20illustration%20of%20a%20clean%20wooden%20desk%20with%20folders%20and%20labeled%20index%20cards%20arranged%20in%20a%20tidy%20grid%2C%20warm%20amber%20terracotta%20and%20sage%20tones%20on%20cream%20paper%20texture%2C%20flat%20modern%20poster%20style%2C%20minimal%20composition%2C%20no%20people%2C%20high%20detail&width=1200&height=900&seq=gt-about-01&orientation=landscape"
                alt="整齐分类的文件夹与索引卡片插画，象征内容分区整理"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-background-200 bg-background-100 p-6 text-center"
              >
                <div className="font-heading text-2xl md:text-3xl font-bold text-primary-700">
                  {item.value}
                </div>
                <div className="mt-2 text-xs md:text-sm text-foreground-600">{item.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10">
            <div>
              <span className="text-xs font-medium text-primary-600 tracking-widest">
                POSITIONING / 网站定位
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
                不做标题党，只做能读完的合集
              </h2>
            </div>
            <div className="space-y-4 text-sm md:text-base leading-relaxed text-foreground-600">
              <p>
                瓜田爆料从 {siteInfo.since} 年开始做同一件事：把散落在各处、看起来毫不相干的娱乐话题，
                按主题重新归类，写成能一口气读完的合集。我们相信，吃瓜的乐趣不应该建立在模糊和猜测之上。
              </p>
              <p>
                网站的每一篇内容都归属于一个明确的分区。你可以从首页的分类入口进入感兴趣的合集，
                也可以在合集页里横向切换，把同一件事的不同侧面连着看完。
              </p>
              <p>
                我们的发展理念很简单：<strong className="text-foreground-900">内容要有信息增量，讨论要有边界</strong>。
                这两条同时成立，一个话题站才值得被长期使用。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-2xl mb-8 md:mb-10">
            <span className="text-xs font-medium text-primary-600 tracking-widest">
              PRINCIPLES / 内容原则
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              我们坚持的四条内容原则
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="w-11 h-11 shrink-0 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-2xl mb-8 md:mb-10">
            <span className="text-xs font-medium text-primary-600 tracking-widest">
              VOICES / 用户口碑
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              读者眼中的瓜田爆料
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700 flex-1">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-200 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-sm font-semibold">
                    {item.initial}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500 mt-0.5">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full bg-background-100 border-t border-background-200 scroll-mt-20">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10">
            <div>
              <span className="text-xs font-medium text-primary-600 tracking-widest">
                CONTACT / 联系与投稿
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
                有线索或有建议，直接告诉我们
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                欢迎提供话题线索、指出内容错误，或建议新增的分类方向。为了提高处理效率，请在来信中说明话题所属分类与可公开查证的背景。
              </p>

              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">邮件联系</div>
                    <a
                      href={`mailto:${siteInfo.email}`}
                      className="mt-1 block text-sm text-primary-700 hover:text-primary-600 transition-colors break-all cursor-pointer"
                    >
                      {siteInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">回复时间</div>
                    <p className="mt-1 text-sm text-foreground-600">{siteInfo.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}